import DownIcon from "@mui/icons-material/ArrowDownward";
import UpIcon from "@mui/icons-material/ArrowUpward";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Stage } from "../../../shared/CaseConfiguration";
import { SettlementRanges } from "../../../shared/CaseModel";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { TonalBadge } from "../../general/TonalBadge";
import { BargainingLineChart } from "./BargainingLineChart";

/** An amount of the variant with how far it is from that of the baseline. */
const ChangedAmount = ({
  label,
  value,
  baseline,
}: {
  label: string;
  value: number;
  baseline: number;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.bargaining",
  });
  const delta = value - baseline;

  return (
    <div>
      <Typography
        variant="caption"
        sx={{ display: "block", fontWeight: 500, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        component="p"
        sx={{
          fontWeight: 700,
          letterSpacing: "-.015em",
          lineHeight: 1.25,
          ...TABULAR_NUMERALS,
        }}
      >
        {currency.format(value)}
      </Typography>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", mt: 0.25 }}
      >
        <TonalBadge
          tone={delta === 0 ? undefined : theme.palette.primary}
          sx={{ py: 0, ...TABULAR_NUMERALS }}
        >
          {delta > 0 && <UpIcon sx={{ fontSize: 13 }} />}
          {delta < 0 && <DownIcon sx={{ fontSize: 13 }} />}
          {delta === 0
            ? `±${currency.format(0)}`
            : `${delta > 0 ? "+" : ""}${currency.format(delta)}`}
        </TonalBadge>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("againstBaseline")}
        </Typography>
      </Stack>
    </div>
  );
};

/**
 * The range a party and one of its opponents could settle in, as the variant
 * moves it: the ends and the midpoint at the first stage of the ranges, each
 * with its change, and both ranges stage by stage.
 */
export const BargainingRange = ({
  name,
  opponentName,
  baseline,
  variant,
}: {
  name: string;
  opponentName: string;
  // the ranges of the case as it is
  baseline: SettlementRanges;
  // the ranges with the parameters of the variant
  variant: SettlementRanges;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const { stage, range: baselineRange } = baseline.perStage[0];
  const { range: variantRange } = variant.perStage[0];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 700 }}>
          {t("analytics.settlement.titleSettlementChart", {
            name,
            other: opponentName,
          })}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("analytics.settlement.bargaining.rangeAt", {
            stage: t("stages." + Stage[stage], Stage[stage]),
          })}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, pt: 2, pb: 2.25 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            columnGap: 3.25,
            rowGap: 2,
            mb: 2.25,
          }}
        >
          <ChangedAmount
            label={t("analytics.settlement.bargaining.evOfParty", { name })}
            value={variantRange.partyEv}
            baseline={baselineRange.partyEv}
          />
          {/* the opponent's value is the other end of the range, seen from its side */}
          <ChangedAmount
            label={t("analytics.settlement.bargaining.evOfParty", {
              name: opponentName,
            })}
            value={-variantRange.opponentEv}
            baseline={-baselineRange.opponentEv}
          />
          <ChangedAmount
            label={t("analytics.settlement.bargaining.midpoint")}
            value={variantRange.mid}
            baseline={baselineRange.mid}
          />
        </Box>
        <BargainingLineChart
          rangeOne={baseline}
          rangeOneLabel={t("analytics.settlement.bargaining.chart.baseline")}
          rangeTwo={variant}
          rangeTwoLabel={t("analytics.settlement.bargaining.chart.variant")}
        />
      </Box>
    </Paper>
  );
};
